import { RightEnum } from "@/type/core/offices/offices.types";
import useUserPicture from "@/utils/hooks/useUserPicture";
import BasicButton from "@components/core/buttons/BasicButton";
import UploadImageField from "@components/core/inputs/UploadImageField";
import FormDetailItem from "@components/core/items/FormDetailItem";
import PageLayout from "@components/core/layouts/PageLayout";
import CardSection from "@components/core/section/CardSection";
import BasicTabs from "@components/core/tabs/BasicTabs";
import BasicTag from "@components/core/tags/BasicTag";
import { ROUTES } from "@routes/Routes";
import { authenticationStore } from "@state/auth/AuthStore";
import CheckIcon from "@static/core/icons/icon-check.svg?react";
import CrossIcon from "@static/core/icons/icon-cross.svg?react";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import { Col, Row } from "antd";
import { useUnit } from "effector-react";
import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import useOfficesToDisplay from "@utils/hooks/useOfficesToDisplay";
import configuration from "@utils/Config";

const MyInformationsScreen: FunctionComponent = () => {
  const rank: number = 1;

  const { t } = useTranslation();
  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);

  const {
    isUserPictureLoading,
    avatar,
    setAvatar,
    fetchedProfilePicture,
    handleSubmitAvatarUpdate,
    cancelAvatarUpdate,
  } = useUserPicture({ userPictures: authStore?.user?.picture });

  const { selectedOffice, handleTabClick, activeKey } = useOfficesToDisplay();

  const officeToDisplay = authStore?.user?.officesForCgu || [];
  const rightsEnum: RightEnum[][] = [
    [
      "Préparation de collectes de tout type",
      "Préparation et envoi de collectes de signature",
    ],
    [
      "Préparation et envoi de collectes de scellement",
      "Signature avec un certificat avancé (LCP) de l'Ordre",
    ],
  ];

  const cardFooter = (
    <i className="font-size-12">
      {t("account.footer")}
      <a
        className={"text-primary not-underline-by-default"}
        href={configuration.comptExpertUrl + "moncompte/mes-infos"}
        target="_blank"
        rel="noreferrer"
      >
        {t("account.comptExpert")}
      </a>
      {"."}
    </i>
  );

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection
          title={t("account.myInformations.informations")}
          footer={cardFooter}
        >
          <>
            <Row gutter={15}>
              <Col span={24} md={8} className="d-flex justify-content-center">
                <UploadImageField
                  showInfos={false}
                  showMetadata={false}
                  field="picture"
                  imageContent={avatar}
                  setImage={setAvatar}
                  imageContentLoading={isUserPictureLoading}
                />
              </Col>
              <Col span={24} md={16}>
                <FormDetailItem
                  title={t("account.myInformations.login")}
                  description={authStore?.user?.login}
                />
                <FormDetailItem
                  title={t("account.myInformations.firstname")}
                  description={authStore?.user?.firstname}
                />
                <FormDetailItem
                  title={t("account.myInformations.lastname")}
                  description={authStore?.user?.lastname}
                />
                <FormDetailItem
                  title={t("account.myInformations.email")}
                  description={authStore?.user?.email}
                />
              </Col>
            </Row>
            <div className="d-flex align-items-center justify-content-end flex-wrap gap-16 mt-30">
              {avatar?.base64Data != fetchedProfilePicture?.base64Data && (
                <BasicButton
                  text={t("buttons.save")}
                  className="mw-160"
                  onClick={() => handleSubmitAvatarUpdate(rank)}
                />
              )}
              {avatar?.base64Data != fetchedProfilePicture?.base64Data && (
                <BasicButton
                  variant="secondary"
                  text={t("buttons.cancel")}
                  className="mw-160"
                  onClick={cancelAvatarUpdate}
                />
              )}
            </div>
          </>
        </CardSection>
        <CardSection
          title={t("account.myInformations.rights")}
          footer={
            selectedOffice?.typeJse === "ComptExpert" ? cardFooter : undefined
          }
        >
          <BasicTabs
            activeKey={activeKey}
            onTabClick={handleTabClick}
            tabs={officeToDisplay.map((office) => {
              return {
                title: office.alias,
                content: (
                  <>
                    {rightsEnum.map((rightDuo, rightsIndex) => (
                      <div
                        key={`rightDuo-${rightsIndex}`}
                        className="d-flex align-items-center flex-wrap gap-16 mb-16"
                      >
                        {rightDuo.map((right, rightDuoIndex) => (
                          <BasicTag
                            key={rightDuoIndex}
                            variant={
                              office.rightsEnum &&
                              office.rightsEnum.includes(right)
                                ? "primary"
                                : "secondary"
                            }
                            label={right}
                            icon={
                              office.rightsEnum &&
                              office.rightsEnum.includes(right) ? (
                                <CheckIcon />
                              ) : (
                                <CrossIcon />
                              )
                            }
                          />
                        ))}
                      </div>
                    ))}
                  </>
                ),
              };
            })}
          />
        </CardSection>
      </>
    </PageLayout>
  );
};

export default MyInformationsScreen;
